import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { formatPhoneDisplay, toE164, isValidE164 } from '../../utils/phoneUtils';
import RichTextEditor from './RichTextEditor';
import AlertMessage from './alertMessage';

/**
 * CommunicationComposer - Shared component for sending SMS, Email, Teams messages, and initiating calls
 *
 * Props:
 * - event: VCXEvent object with customer contact info (for healthcare context)
 * - onCommunicationSent: Callback when communication is successfully sent (channel, data, response)
 * - onCancel: Callback when user cancels
 * - compact: Boolean for compact mode (default: false)
 * - isAnkrAdmin: Boolean to show/hide advanced fields (workflow IDs, etc.) (default: false)
 * - deferSend: Boolean - if true, queues communication instead of sending immediately (default: false)
 * - onCommunicationQueued: Callback when communication is queued (for deferSend mode)
 *
 * NEW - Sales context props:
 * - entityType: 'vcxevent' | 'prospect' | 'lead' | 'opportunity' (determines tracking behavior)
 * - entityId: UUID of the entity
 * - entity: Full entity object (for extracting email, phone, etc.)
 */
const CommunicationComposer = ({
  event,
  onCommunicationSent,
  onCancel,
  compact = false,
  isAnkrAdmin = false,
  deferSend = false,
  onCommunicationQueued,
  // NEW: Sales context
  entityType = 'vcxevent',
  entityId = null,
  entity = null
}) => {
  const [activeChannel, setActiveChannel] = useState(entityType !== 'vcxevent' ? ('email'):(null)); // null, 'sms', 'email', 'teams', 'call'
  const [communicationData, setCommunicationData] = useState({
    sms: { phone: '', message: '', workflow_id: 'ankr-vcx-notifications-master' },
    email: { email: '', subject: '', body: '', workflow_id: 'ankr-vcx-notifications-master' },
    teams: { channel_id: '', user_id: '', message: '', message_type: 'channel' },
    call: { to_phone: '', purpose: '' }
  });
  const [sending, setSending] = useState(false);
  const [alertState, setAlertState] = useState({ show: false, type: '', title: '', message: '' });
  const [hipaaAcknowledged, setHipaaAcknowledged] = useState({ sms: true, email: true });
  const [emailValidation, setEmailValidation] = useState({ isValid: true, message: '' });

  // NEW: Sales email tracking
  const [trackOpens, setTrackOpens] = useState(entityType !== 'vcxevent');

  // Store organization info for multi-tenant Novu workflows
  const [orgMetadata, setOrgMetadata] = useState({ customer_id: null, customer_stub: null });

  // Initialize communication data from event OR entity
  useEffect(() => {
    const sourceEntity = event || entity;

    if (sourceEntity) {
      // Extract phone and email based on context
      let phone = '';
      let email = '';

      if (entityType === 'vcxevent') {
        // VCX Event context
        phone = sourceEntity.customer_phone ? formatPhoneDisplay(sourceEntity.customer_phone) : '';
        email = sourceEntity.customer_email || '';
      } else {
        // Sales context (prospect, lead, opportunity)
        // Try to get email from prospects array first
        const prospects = sourceEntity.prospects || [];
        const prospectWithEmail = prospects.find(p => p.email);

        if (prospectWithEmail?.email) {
          email = prospectWithEmail.email;
        } else if (sourceEntity.prospect_email) {
          email = sourceEntity.prospect_email;
        }

        // Try to get phone from prospects array first
        const prospectWithPhone = prospects.find(p => p.phone);
        if (prospectWithPhone?.phone) {
          phone = formatPhoneDisplay(prospectWithPhone.phone);
        } else if (sourceEntity.prospect_phone) {
          phone = formatPhoneDisplay(sourceEntity.prospect_phone);
        }

        // Extract organization metadata for multi-tenant Novu workflows
        if (sourceEntity.sales_organization && sourceEntity.sales_organization.name) {
          setOrgMetadata({
            customer_id: sourceEntity.sales_organization.id,
            customer_stub: sourceEntity.sales_organization.name.toLowerCase().replace(/\s+/g, '_')
          });
        }
      }

      setCommunicationData({
        sms: {
          phone: phone,
          message: '',
          workflow_id: entityType === 'vcxevent' ? 'vcx-event-sms-notification' : 'ankr-vcx-notifications-master'
        },
        email: {
          email: email,
          subject: '',
          body: '',
          workflow_id: entityType === 'vcxevent' ? 'vcx-event-email-notification' : 'ankr-vcx-notifications-master'
        },
        teams: {
          channel_id: '',
          user_id: '',
          message: '',
          message_type: 'channel'
        },
        call: {
          to_phone: phone,
          purpose: ''
        }
      });
    }
  }, [event, entity, entityType]);

  // Email validation function
  const validateEmail = (email) => {
    if (!email) {
      return { isValid: false, message: 'Email is required' };
    }
    // RFC 5322 compliant email regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Invalid email format' };
    }
    return { isValid: true, message: '' };
  };

  const handleChange = (channel, field, value) => {
    // For phone fields, only allow digits and phone formatting characters
    // DO NOT format here to avoid cursor jumping - format happens on blur
    if (field === 'phone' || field === 'to_phone') {
      value = value.replace(/[^\d+\-() ]/g, '');
    }

    // Validate email on change
    if (channel === 'email' && field === 'email') {
      const validation = validateEmail(value);
      setEmailValidation(validation);
    }

    setCommunicationData(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [field]: value
      }
    }));
    setAlertState({ show: false, type: '', title: '', message: '' });
  };

  // Format phone on blur - following PhoneNumberManager pattern
  const handlePhoneBlur = (channel, field) => {
    const currentValue = communicationData[channel][field];
    if (currentValue) {
      const e164 = toE164(currentValue);
      if (isValidE164(e164)) {
        setCommunicationData(prev => ({
          ...prev,
          [channel]: {
            ...prev[channel],
            [field]: formatPhoneDisplay(e164)
          }
        }));
      }
    }
  };

  // Helper to build communication record structure
  const buildCommunicationRecord = (channel, data) => {
    switch (channel) {
      case 'sms':
        return {
          channel: 'sms',
          to: toE164(data.phone), // Convert to E.164 for sending
          message: data.message,
          workflow_id: data.workflow_id,
          status: 'queued',
          queued_at: new Date().toISOString()
        };

      case 'email':
        return {
          channel: 'email',
          to: data.email,
          email: data.email,  // Include both 'to' and 'email' for consistency
          subject: data.subject,
          body: data.body,
          html_body: data.body,  // Store HTML body for tracking display
          workflow_id: data.workflow_id,
          status: 'queued',
          queued_at: new Date().toISOString()
        };

      case 'teams':
        return {
          channel: 'teams',
          message_type: data.message_type,
          to: data.message_type === 'channel' ? data.channel_id : data.user_id,
          message: data.message,
          status: 'queued',
          queued_at: new Date().toISOString()
        };

      case 'call':
        return {
          channel: 'phone',
          to: toE164(data.to_phone), // Convert to E.164 for sending
          purpose: data.purpose,
          status: 'queued',
          queued_at: new Date().toISOString()
        };

      default:
        return {};
    }
  };

  const handleSend = async () => {
    if (!activeChannel) return;

    const data = communicationData[activeChannel];

    // Validate based on channel
    try {
      switch (activeChannel) {
        case 'sms':
          if (!data.phone || !data.message) {
            throw new Error('Phone number and message are required');
          }
          if (!hipaaAcknowledged.sms) {
            throw new Error('You must acknowledge the HIPAA compliance statement');
          }
          break;
        case 'email':
          if (!data.email || !data.subject) {
            throw new Error('Email address and subject are required');
          }
          if (!hipaaAcknowledged.email) {
            throw new Error('You must acknowledge the HIPAA compliance statement');
          }
          break;
        case 'teams':
          if (!data.message) {
            throw new Error('Message is required');
          }
          if (data.message_type === 'channel' && !data.channel_id) {
            throw new Error('Channel ID is required');
          }
          if (data.message_type === 'user' && !data.user_id) {
            throw new Error('User ID is required');
          }
          break;
        case 'call':
          if (!data.to_phone) {
            throw new Error('Phone number is required');
          }
          break;
        default:
          throw new Error('Invalid communication channel');
      }
    } catch (err) {
      setAlertState({ show: true, type: 'danger', title: '', message: err.message });
      return;
    }

    // If deferSend is true, just queue the communication without sending
    if (deferSend) {
      const communicationRecord = buildCommunicationRecord(activeChannel, data);

      if (onCommunicationQueued) {
        onCommunicationQueued(activeChannel, communicationRecord);
      }

      // Show success message for queued communication
      const channelNames = { sms: 'SMS', email: 'Email', teams: 'Teams message', call: 'Call' };
      setAlertState({
        show: true,
        type: 'success',
        title: '',
        message: `${channelNames[activeChannel]} queued successfully!`
      });

      // Reset form and close
      setActiveChannel(null);
      setHipaaAcknowledged({ sms: true, email: true });
      return;
    }

    // Otherwise, send immediately (original behavior)
    setSending(true);
    setAlertState({ show: false, type: '', title: '', message: '' });

    try {
      let response;
      let communicationRecord = {};

      switch (activeChannel) {
        case 'sms':
          response = await adminApi.sendSMS({
            recipient_type: entityType === 'vcxevent' ? 'vcx_event' : entityType,
            recipient_id: entityType === 'vcxevent' ? event?.id : entityId,
            workflow_id: data.workflow_id,
            phone: toE164(data.phone), // Convert to E.164 for API
            message: data.message,
            // Add organization metadata for multi-tenant Novu workflows
            ...(orgMetadata.customer_id && {
              customer_id: orgMetadata.customer_id,
              customer_stub: orgMetadata.customer_stub
            })
          });
          communicationRecord = {
            channel: 'sms',
            to: toE164(data.phone), // Convert to E.164 for storage
            message: data.message,
            workflow_id: data.workflow_id,
            status: 'sent',
            sent_at: new Date().toISOString()
          };
          break;

        case 'email': {
          // Build email request
          const emailRequest = {
            recipient_type: entityType === 'vcxevent' ? 'vcx_event' : entityType,
            recipient_id: entityType === 'vcxevent' ? event?.id : entityId,
            workflow_id: data.workflow_id,
            email: data.email,
            subject: data.subject,
            body: data.body,
            track_opens: trackOpens  // Enable tracking for all emails (VCX + Sales)
          };

          // Add sales entity tracking metadata (not needed for VCX events)
          if (entityType !== 'vcxevent' && entityId) {
            emailRequest.entity_type = entityType;
            emailRequest.entity_id = entityId;
          }

          // Add organization metadata for multi-tenant Novu workflows
          if (orgMetadata.customer_id) {
            emailRequest.customer_id = orgMetadata.customer_id;
            emailRequest.customer_stub = orgMetadata.customer_stub;
          }

          response = await adminApi.sendEmail(emailRequest);
          break;
        }

        case 'teams':
          response = await adminApi.sendTeamsMessage({
            channel_id: data.message_type === 'channel' ? data.channel_id : undefined,
            user_id: data.message_type === 'user' ? data.user_id : undefined,
            message: data.message,
            message_type: data.message_type
          });
          break;

        case 'call':
          response = await adminApi.initiateCall({
            to_phone: toE164(data.to_phone), // Convert to E.164 for API
            purpose: data.purpose
          });
          break;

        default:
          throw new Error('Invalid communication channel');
      }

      // Build communication record from form data
      communicationRecord = buildCommunicationRecord(activeChannel, data);
      communicationRecord.status = 'sent'; // Override status for immediate sends
      communicationRecord.sent_at = new Date().toISOString();

      // Add response-specific data
      if (activeChannel === 'email' && response.data) {
        if (response.data.tracking_id) {
          communicationRecord.tracking_id = response.data.tracking_id;
          communicationRecord.email_opened = false;
          communicationRecord.opened_at = null;
        }
        if (response.data.activity_id) {
          communicationRecord.activity_id = response.data.activity_id;
        }
      } else if (activeChannel === 'call' && response.data) {
        communicationRecord.call_sid = response.data.call_sid;
        communicationRecord.status = response.data.status || 'queued';
      }

      // Callback to parent with communication details
      if (onCommunicationSent) {
        onCommunicationSent(activeChannel, communicationRecord, response);
      }

      // Show success message
      const channelNames = { sms: 'SMS', email: 'Email', teams: 'Teams message', call: 'Call' };
      setAlertState({
        show: true, type: 'success', title: '',
        message: `${channelNames[activeChannel]} sent successfully!`
      });

      // Reset form and close
      setActiveChannel(null);
    } catch (err) {
      setAlertState({ show: true, type: 'danger', title: '', message: err.response?.data?.detail || err.message || 'Failed to send communication' });
    } finally {
      setSending(false);
    }
  };

  const renderChannelButtons = () => (
    <div className={compact ? "d-flex gap-2 mb-2" : "mb-3"}>
      {!compact && <p className="card-title text-primary mb-2">Communicate, Analyze, Track</p>}
      <div className="d-flex gap-2 mt-2" role="group">
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => setActiveChannel('sms')}
        >
          📱 SMS
        </button>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => setActiveChannel('email')}
        >
          📧 Email
        </button>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => setActiveChannel('teams')}
        >
          💬 Teams
        </button>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => setActiveChannel('call')}
        >
          📞 Call
        </button>
      </div>
    </div>
  );

  const renderSMSComposer = () => (
    <div className="card mb-3 p-3">
      <h5 className="mb-2">📱 Send SMS</h5>
      <div className="mb-2">
        <label className="form-label">Phone Number</label>
        <input
          type="tel"
          className="form-control form-control-sm"
          value={communicationData.sms.phone}
          onChange={(e) => handleChange('sms', 'phone', e.target.value)}
          onBlur={() => handlePhoneBlur('sms', 'phone')}
          placeholder="+1 (xxx) xxx-xxxx"
        />
      </div>
      {isAnkrAdmin && (
        <div className="mb-2">
          <label className="form-label">Novu Workflow ID <span className="badge bg-warning text-dark">Admin Only</span></label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={communicationData.sms.workflow_id}
            onChange={(e) => handleChange('sms', 'workflow_id', e.target.value)}
            placeholder="ankr-vcx-notifications-master"
          />
          <small className="text-muted">The workflow must exist and be active in your Novu dashboard</small>
        </div>
      )}
      <div className="mb-2">
        <div className="d-flex justify-content-between">
          <label className="form-label">Message </label>
          <span className="text-muted ms-2 small">
            {communicationData.sms.message.length}/1000 characters
          </span>
        </div>
        <textarea
          className="form-control form-control-sm"
          rows="3"
          value={communicationData.sms.message}
          onChange={(e) => {
            if (e.target.value.length <= 1000) {
              handleChange('sms', 'message', e.target.value);
            }
          }}
          placeholder="Your appointment is confirmed..."
          maxLength={1000}
        />
      </div>
      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="sms-hipaa-acknowledgment"
            checked={hipaaAcknowledged.sms}
            onChange={(e) => setHipaaAcknowledged(prev => ({ ...prev, sms: e.target.checked }))}
          />
          <label className="form-check-label text-muted" htmlFor="sms-hipaa-acknowledgment">
            This message does not contain protected health information (PHI)
            <span className="text-primary ms-1" style={{ cursor: 'help', fontSize: '0.9em' }}
              title="HIPAA-compliant SMS/email are not currently enabled for your organization. Only send messages that do not contain protected health information (PHI)."
            > ⓘ
            </span>
          </label>
        </div>
      </div>
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSend}
          disabled={sending || !communicationData.sms.phone || !communicationData.sms.message || !hipaaAcknowledged.sms}
        >
          {sending ? 'Sending...' : (deferSend ? 'Queue SMS' : 'Send SMS')}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => { setActiveChannel(null); if (onCancel) onCancel(); }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderEmailComposer = () => (
    <div className="card mb-3 p-3">
      <h5 className="mb-2">📧 Send Email</h5>
      <div className="mb-2">
        <label className="form-label">Email Address</label>
        <input
          type="email"
          className={`form-control form-control-sm ${!emailValidation.isValid && communicationData.email.email ? 'is-invalid' : ''}`}
          value={communicationData.email.email}
          onChange={(e) => handleChange('email', 'email', e.target.value)}
          placeholder="patient@example.com"
        />
        {!emailValidation.isValid && communicationData.email.email && (
          <div className="invalid-feedback d-block">
            {emailValidation.message}
          </div>
        )}
      </div>
      {isAnkrAdmin && (
        <div className="mb-2">
          <label className="form-label">Novu Workflow ID <span className="badge bg-warning text-dark">Admin Only</span></label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={communicationData.email.workflow_id}
            onChange={(e) => handleChange('email', 'workflow_id', e.target.value)}
            placeholder="ankr-vcx-notifications-master"
          />
          <small className="text-muted">The workflow must exist and be active in your Novu dashboard</small>
        </div>
      )}
      <div className="mb-2">
        <label className="form-label">Subject</label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={communicationData.email.subject}
          onChange={(e) => handleChange('email', 'subject', e.target.value)}
          placeholder="Appointment Reminder"
        />
      </div>
      <div className="mb-2">
        <label className="form-label">Body</label>
        <RichTextEditor
          value={communicationData.email.body}
          onChange={(html) => handleChange('email', 'body', html)}
          placeholder="Your appointment is tomorrow at 2pm..."
          minHeight={250}
        />
      </div>
      {entityType !== 'vcxevent' && (
        <div className="mb-2">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="email-track-opens"
              checked={trackOpens}
              onChange={(e) => setTrackOpens(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="email-track-opens">
              Track email opens
              <span className="text-muted ms-1 small">(embeds tracking pixel)</span>
            </label>
          </div>
        </div>
      )}
      <div className="mb-3">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="email-hipaa-acknowledgment"
            checked={hipaaAcknowledged.email}
            onChange={(e) => setHipaaAcknowledged(prev => ({ ...prev, email: e.target.checked }))}
          />
          <label className="form-check-label text-muted" htmlFor="email-hipaa-acknowledgment">
            This message does not contain protected health information (PHI)
            <span className="text-primary ms-1" style={{ cursor: 'help', fontSize: '0.9em' }}
              title="HIPAA-compliant SMS/email are not currently enabled for your organization. Only send messages that do not contain protected health information (PHI)."
            >
              ⓘ
            </span>
          </label>
        </div>
      </div>
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSend}
          disabled={sending || !communicationData.email.email || !emailValidation.isValid || !communicationData.email.subject || !hipaaAcknowledged.email}
        >
          {sending ? 'Sending...' : (deferSend ? 'Queue Email' : 'Send Email')}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => { setActiveChannel(null); if (onCancel) onCancel(); }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderTeamsComposer = () => (
    <div className="card mb-3 p-3 bg-light">
      <h5 className="mb-2">💬 Send Teams Message</h5>
      {isAnkrAdmin && (
        <div className="mb-2">
          <label className="form-label">Message Type <span className="badge bg-warning text-dark">Admin Only</span></label>
          <select
            className="form-select form-select-sm"
            value={communicationData.teams.message_type}
            onChange={(e) => handleChange('teams', 'message_type', e.target.value)}
          >
            <option value="channel">Channel</option>
            <option value="user">Direct Message</option>
          </select>
        </div>
      )}
      {isAnkrAdmin && communicationData.teams.message_type === 'channel' && (
        <div className="mb-2">
          <label className="form-label">Teams Channel ID <span className="badge bg-warning text-dark">Admin Only</span></label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={communicationData.teams.channel_id}
            onChange={(e) => handleChange('teams', 'channel_id', e.target.value)}
            placeholder="teams_channel_id"
          />
          <small className="text-muted">Contact Ankr admin to configure default channel</small>
        </div>
      )}
      {isAnkrAdmin && communicationData.teams.message_type === 'user' && (
        <div className="mb-2">
          <label className="form-label">Teams User ID <span className="badge bg-warning text-dark">Admin Only</span></label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={communicationData.teams.user_id}
            onChange={(e) => handleChange('teams', 'user_id', e.target.value)}
            placeholder="teams_user_id"
          />
          <small className="text-muted">Contact Ankr admin to configure default user</small>
        </div>
      )}
      {!isAnkrAdmin && (
        <div className="alert alert-info text-sm mb-2">
          ℹ️ Teams messages will be sent to your organization&apos;s configured channel. Contact Ankr admin to change settings.
        </div>
      )}
      <div className="mb-2">
        <label className="form-label">Message</label>
        <textarea
          className="form-control form-control-sm"
          rows="3"
          value={communicationData.teams.message}
          onChange={(e) => handleChange('teams', 'message', e.target.value)}
          placeholder="New patient inquiry received..."
        />
      </div>
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSend}
          disabled={
            sending ||
            !communicationData.teams.message ||
            (communicationData.teams.message_type === 'channel' && !communicationData.teams.channel_id) ||
            (communicationData.teams.message_type === 'user' && !communicationData.teams.user_id)
          }
        >
          {sending ? 'Sending...' : (deferSend ? 'Queue Teams Message' : 'Send Teams Message')}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => { setActiveChannel(null); if (onCancel) onCancel(); }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderCallInitiator = () => (
    <div className="card mb-3 p-3 bg-light">
      <h5 className="mb-2">📞 Initiate Call</h5>
      <div className="mb-2">
        <label className="form-label">Phone Number</label>
        <input
          type="tel"
          className="form-control form-control-sm"
          value={communicationData.call.to_phone}
          onChange={(e) => handleChange('call', 'to_phone', e.target.value)}
          onBlur={() => handlePhoneBlur('call', 'to_phone')}
          placeholder="+1 (xxx) xxx-xxxx"
        />
      </div>
      <div className="mb-2">
        <label className="form-label">Purpose (Optional)</label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={communicationData.call.purpose}
          onChange={(e) => handleChange('call', 'purpose', e.target.value)}
          placeholder="Follow-up on appointment"
        />
      </div>
      {isAnkrAdmin ? (
        <div className="alert alert-info text-sm mb-2">
          ℹ️ <strong>Admin:</strong> This will initiate an outbound call using Twilio. Ensure credentials are configured.
        </div>
      ) : (
        <div className="alert alert-info text-sm mb-2">
          ℹ️ This will initiate an outbound call. You will receive the call on your configured phone.
        </div>
      )}
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSend}
          disabled
          // disabled={sending || !communicationData.call.to_phone}
        >
          {sending ? 'Calling...' : (deferSend ? 'Queue Call' : 'Initiate Call')}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => { setActiveChannel(null); if (onCancel) onCancel(); }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="communication-composer">
      {!activeChannel && renderChannelButtons()}
      {activeChannel === 'sms' && renderSMSComposer()}
      {activeChannel === 'email' && renderEmailComposer()}
      {activeChannel === 'teams' && renderTeamsComposer()}
      {activeChannel === 'call' && renderCallInitiator()}
    </div>
  );
};

export default CommunicationComposer;
